import { DragonflyController } from "./DragonflyController";
import * as THREE from "three";
import { DragonflyOrbitController } from "./DragonflyOrbitController";
import { Twist } from "../../../../models/SyntheticMap";
import { Vec2, Vec3 } from "../../../../models/SyntheticMap";
import { constrain, normalizeAngleToPi } from "../../../../utils/spatial";
import { getLocationFromMgrs, getMrgsFromLocation } from "../../../../components/actorPropertyComponents/MapLocator";
function distanceToLine(lineStart: Vec2, lineEnd: Vec2, point: Vec2 = { x: 0, y: 0 }): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const denominator = Math.sqrt(dx * dx + dy * dy);

  if (denominator === 0) {
    // Points are the same; return positive distance to lineStart
    return Math.sqrt(
      (point.x - lineStart.x) * (point.x - lineStart.x) +
      (point.y - lineStart.y) * (point.y - lineStart.y)
    );
  }

  const px = point.x - lineStart.x;
  const py = point.y - lineStart.y;
  const cross = dx * py - dy * px;
  return cross / denominator;
}

export class DragonflyNavigateController extends DragonflyController {
  public mgrsPath: string[] = [];
  public pointPath: THREE.Vector3[] = [];
  private lastPoint!: THREE.Vector3;
  status: string = 'Navigating';
  constructor(
    public obj: THREE.Object3D,
    public flightPlan: string | string[], // MGRS string for the destination
    public cruisingAltitude = 100, // meters
    public cruisingSpeed = 2.0, // m/s
  ) {
    super();
    const posit = getMrgsFromLocation(obj.position as THREE.Vector3);
    this.mgrsPath.push(posit);
    if (typeof flightPlan === 'string') {
      this.mgrsPath.push(flightPlan);
    } else {
      this.mgrsPath.push(...flightPlan); // If it's an array, add all elements
    }
    this.mapCourseToTarget();
  }
  private mapCourseToTarget() {
    this.pointPath = [];
    for (const mgrs of this.mgrsPath) {
      const loc = getLocationFromMgrs(mgrs);
      loc.z = this.cruisingAltitude; // Set altitude to cruising altitude
      this.pointPath.push(new THREE.Vector3(loc.x, loc.y, loc.z));
    }
    this.lastPoint = this.pointPath[this.pointPath.length - 1];
    // Create a THREE.Line or similar to visualize the path if needed
    // this.obj.add(line); // Add the path line to the object
  }

  private line: THREE.Line | null = null;
  public visualization(on: boolean): void {
    if (this.line) {
      this.obj.parent?.remove(this.line); // Remove the previous line if it exists
      this.line = null;
    }
    if (on) {
      const geometry = new THREE.BufferGeometry().setFromPoints(this.pointPath);
      const material = new THREE.LineBasicMaterial({ color: 0xff0000 });
      this.line = new THREE.Line(geometry, material);
      this.obj.parent?.add(this.line); // Add the path line to the parent of the object    
    }
  }
  private altGain = 0.1; // Altitude control gain
  private yawGain = 2.0; // Yaw control gain
  private distanceGain = 0.25; // Distance control gain
  private closeDistance = 5.0; // Distance to consider close to the next point
  public computeTwist(dt: number): Twist | DragonflyController {
    let point1 = this.pointPath[0];
    let point2 = this.pointPath[1];
    if (!point1 || !point2) {
      // debugger;
      const orbitCenter = this.lastPoint.clone();
      orbitCenter.z = 0;
      return new DragonflyOrbitController(
        this.obj,
        orbitCenter,
        75.0,
        this.cruisingAltitude,
        this.cruisingSpeed * 0.5);
    }
    const localPoint1 = this.obj.worldToLocal(point1.clone());
    const localPoint2 = this.obj.worldToLocal(point2.clone());
    let dist = localPoint2.length();
    let p = point2.clone();
    for (let i = 2; i < this.pointPath.length; i++) {
      const nextPoint = this.pointPath[i].clone();
      const leg = nextPoint.sub(p);
      dist += leg.length();
      p = this.pointPath[i].clone();
    }
    const time = dist / (this.cruisingSpeed * 2);
    this.status = `ETA: ${time.toFixed(2)}s`;
    const localDirection = Math.atan2(localPoint2.y - localPoint1.y, localPoint2.x - localPoint1.x);
    if (localPoint2.length() < this.closeDistance || (
      Math.abs(localDirection) < Math.PI / 2 && localPoint2.x < this.closeDistance && Math.abs(localPoint2.y) < this.closeDistance
    )) {
      // If the next point is too close, remove it and continue
      this.pointPath.shift();
      return this.computeTwist(dt); // Recompute with the next point
    }
    const distance = distanceToLine(localPoint1, localPoint2);
    // console.log("Distance to line: ", distance);
    const distanceError = constrain(-distance * this.distanceGain, -Math.PI / 2, Math.PI / 2); // Constrain distance error to a reasonable range
    const localCourse = normalizeAngleToPi(localDirection + distanceError);
    const desired_yaw_rate = localCourse * this.yawGain; // Yaw rate control based on course error
    const alt_error = this.cruisingAltitude - this.obj.position.z; // positive if below target
    let desired_vz = this.altGain * alt_error;
    let boost = 1.0;
    if (Math.abs(desired_vz) < 0.1) {
      boost = 2.0;
    }
    return {
      linear: { x: this.cruisingSpeed * boost, y: 0, z: desired_vz },
      angular: { x: 0, y: 0, z: desired_yaw_rate }
    };
  }

  public getStatus(): string {
    return this.status;
  }

}